/**
 * COL-529: is a KeyNest partner shop within 15 km of the property? Drives
 * whether the "KeyNest partner" key-handover option is offered in the V3
 * onboarding checklist. The distance math and KEYNEST_API_KEY stay server-side
 * in the `keynest-nearby` edge function — this hook only reads the verdict.
 *
 * Falls back to `available: false` (→ in-person handover) on missing
 * coordinates, an API error, or no shop in range.
 */
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export type KeyNestAvailability = {
  available: boolean;
  nearest?: { name: string; km: number };
};

const UNAVAILABLE: KeyNestAvailability = { available: false };

export function useKeyNestAvailability(
  lat: number | null,
  lng: number | null
): KeyNestAvailability {
  const { data } = useQuery({
    queryKey: ["v2", "keynest-nearby", lat, lng],
    enabled: typeof lat === "number" && typeof lng === "number",
    staleTime: 60 * 60_000, // partner footprint barely moves
    queryFn: async (): Promise<KeyNestAvailability> => {
      const { data, error } = await supabase.functions.invoke(
        "keynest-nearby",
        {
          body: { lat, lng },
        }
      );
      if (error || !data || typeof data.available !== "boolean")
        return UNAVAILABLE;
      return data as KeyNestAvailability;
    },
  });
  return data ?? UNAVAILABLE;
}
